// crewbit-admin/src/hooks/useInvoiceDraft.ts
//
// Auto-save lifecycle for the §InvoiceAuthoring screen (admin parallel).
// Mirrors crewbit-mobile/hooks/useInvoiceDraft.ts shape; admin variant
// uses window.dispatchEvent for MONEY_MUTATION_EVENT instead of
// DeviceEventEmitter.
//
// `save()` returns the resolved invoice id + accepts an optional state
// override (per memory feedback_save_override_api.md). Mobile parallel ships
// the same shape post-Session-131. Note one remaining asymmetry: this hook
// also accepts `initialDraftInvoiceId` for resume-from-detail-panel Edit
// lift; mobile doesn't have a resume UX surface yet (deferred
// §InvoiceAuthoring.DraftResumeFromList).
//
// Discriminator pattern (Tier 2-UI Phase D-admin §T2UI.PhaseD-mobile.D3):
// when the operator hasn't typed a tax-rate override (taxRateOverride ===
// null), DON'T pass p_tax_rate to create_invoice / update_invoice. Server
// resolves via clients.default_tax_rate_override → bfs.hst_rate → 0.13.
// When the operator types an override, pass it. UI never mediates resolved
// defaults.

import { useCallback, useEffect, useRef, useState } from 'react';
import { callMoneyRpc } from '../lib/moneyRpc';
import { linesToCents, type AuthoringLine } from '../lib/invoiceLineItems';
import { useActiveBusinessStore } from './useActiveBusiness';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';
import type {
  ClientDefaultsResolved,
  ClientDefaultsSources,
  ClientDefaultsReasoning,
  LateFeePolicy,
} from '../types/clientDefaults';

const AUTOSAVE_DEBOUNCE_MS = 5000;

export interface AuthoringDraftState {
  clientId: string | null;
  clientName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  clientAddress: string | null;
  lines: AuthoringLine[];
  /** UI percent (13 for 13%). null = operator hasn't typed; let server resolve. */
  taxRateOverride: number | null;
  /**
   * Phase D v2 override discriminators (admin parallel of mobile §D-v2.D1).
   * null = let server resolve via 4-tier chain. Non-null = pass to RPC.
   *
   * Late-fee fields are paired (polymorphic CHECK in substrate Migration 275):
   * both must be set together OR both null. Mid-state collapses to null/null
   * at save() boundary per Decision §D-v2.D4.
   */
  paymentTermsDaysOverride: number | null;
  depositPercentageOverride: number | null;
  lateFeePolicyOverride: LateFeePolicy | null;
  lateFeeAmountOverride: number | null;
  issueDate: string;
  dueDate: string | null;
  notes: string;
  isSalesReceipt: boolean;
  sourceQuoteId: string | null;
  /**
   * Per-client overrides resolved at client-pick boundary via
   * get_client_defaults RPC. AuthoringReviewSection reads for display +
   * pre-fill; save() never passes resolved defaults back — server is
   * authoritative (Decision §T2UI.PhaseD-mobile.D2 + D3).
   */
  clientDefaults: {
    resolved: ClientDefaultsResolved | null;
    sources: ClientDefaultsSources | null;
    reasoning: ClientDefaultsReasoning | null;
    isLoading: boolean;
    error: string | null;
  };
}

/**
 * Three-tier display resolver for tax rate (UI percent).
 * 1. Operator-typed override (`state.taxRateOverride`) wins.
 * 2. Else resolved value from per-client defaults envelope (decimal × 100).
 * 3. Else hardcoded 13% (matches server-side hardcoded fallback).
 *
 * Used by AuthoringLineItemsSection + AuthoringReviewSection + the cash-
 * path subtotal calc — keeps the display cascade in one place so all
 * consumers stay consistent.
 */
export function resolveTaxRatePercent(state: AuthoringDraftState): number {
  if (state.taxRateOverride !== null) return state.taxRateOverride;
  const resolved = state.clientDefaults.resolved;
  if (resolved) return resolved.tax_rate_resolved * 100;
  return 13;
}

/** Three-tier resolver for payment terms (days). Override → resolved → 14. */
export function resolvePaymentTermsDays(state: AuthoringDraftState): number {
  if (state.paymentTermsDaysOverride !== null) return state.paymentTermsDaysOverride;
  const resolved = state.clientDefaults.resolved;
  if (resolved) return resolved.payment_terms_days;
  return 14;
}

/** Three-tier resolver for deposit % (UI integer 0..100). Override → resolved → 0. */
export function resolveDepositPercentage(state: AuthoringDraftState): number {
  if (state.depositPercentageOverride !== null) return state.depositPercentageOverride;
  const resolved = state.clientDefaults.resolved;
  if (resolved) return resolved.deposit_percentage;
  return 0;
}

/**
 * Late-fee paired-field display resolver. Override pair → resolved client
 * defaults → 'none'. Mid-state on override (one set, other null) falls
 * through to resolved.
 */
export function resolveLateFeeDisplay(state: AuthoringDraftState): {
  policy: LateFeePolicy | null;
  amount: number | null;
  source: 'override' | 'resolved' | 'none';
} {
  const op = state.lateFeePolicyOverride;
  const oa = state.lateFeeAmountOverride;
  if (op !== null && oa !== null) {
    return { policy: op, amount: oa, source: 'override' };
  }
  const resolved = state.clientDefaults.resolved;
  if (resolved && resolved.late_fee_policy !== null && resolved.late_fee_amount !== null) {
    return {
      policy: resolved.late_fee_policy,
      amount: resolved.late_fee_amount,
      source: 'resolved',
    };
  }
  return { policy: null, amount: null, source: 'none' };
}

export interface SaveResult {
  success: boolean;
  error?: string;
  /** Resolved invoice id on success (create path: minted; update path: existing). */
  invoiceId?: string;
  /** Resolved invoice number on success — only set when this call minted the draft. */
  invoiceNumber?: string;
}

export interface UseInvoiceDraftReturn {
  save: (override?: Partial<AuthoringDraftState>) => Promise<SaveResult>;
  isAutoSaving: boolean;
  lastSavedAt: string | null;
  draftInvoiceId: string | null;
  draftInvoiceNumber: string | null;
  error: Error | null;
}

interface CreateInvoiceResultData {
  invoice_id: string;
  invoice_number: string;
  status: string;
  already_created: boolean;
}

export interface UseInvoiceDraftOptions {
  /** Pre-existing draft id for resume-from-detail-panel Edit (§3e). */
  initialDraftInvoiceId?: string;
  /** Pre-existing invoice number to surface as panel subtitle on resume. */
  initialDraftInvoiceNumber?: string | null;
}

export function useInvoiceDraft(
  state: AuthoringDraftState,
  options?: UseInvoiceDraftOptions,
): UseInvoiceDraftReturn {
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const [draftInvoiceId, setDraftInvoiceId] = useState<string | null>(
    options?.initialDraftInvoiceId ?? null,
  );
  const [draftInvoiceNumber, setDraftInvoiceNumber] = useState<string | null>(
    options?.initialDraftInvoiceNumber ?? null,
  );
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const [idempotencyKey] = useState<string>(() => crypto.randomUUID());

  const lastSerializedState = useRef<string>('');
  const inFlightRef = useRef<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<AuthoringDraftState>(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const save = useCallback(async (
    override?: Partial<AuthoringDraftState>,
  ): Promise<SaveResult> => {
    if (!businessId) return { success: false, error: 'no_active_business' };
    if (inFlightRef.current) return { success: false, error: 'in_flight' };
    inFlightRef.current = true;
    setIsAutoSaving(true);
    setError(null);

    const s: AuthoringDraftState = override
      ? { ...stateRef.current, ...override }
      : stateRef.current;
    // Discriminator: operator-typed override → pass p_tax_rate; null →
    // omit so server resolves via clients.default_tax_rate_override →
    // bfs.hst_rate → 0.13 (Decision §T2UI.PhaseD-mobile.D3).
    const taxRateArg = s.taxRateOverride === null ? null : s.taxRateOverride / 100;
    // Phase D v2 — same discriminator pattern across 4 additional fields
    // (Decision §D-v2.D1). Late-fee paired-field invariant collapses mid-
    // state to null/null at save boundary so substrate's polymorphic CHECK
    // can't fire (Decision §D-v2.D4 + Failure Mode #4).
    const paymentTermsArg = s.paymentTermsDaysOverride;
    const depositArg = s.depositPercentageOverride;
    const lateFeePaired =
      s.lateFeePolicyOverride !== null && s.lateFeeAmountOverride !== null;
    const lateFeePolicyArg = lateFeePaired ? s.lateFeePolicyOverride : null;
    const lateFeeAmountArg = lateFeePaired ? s.lateFeeAmountOverride : null;
    try {
      if (draftInvoiceId === null) {
        const result = await callMoneyRpc<CreateInvoiceResultData>('create_invoice', {
          p_business_id: businessId,
          p_client_id: s.clientId,
          p_line_items: linesToCents(s.lines),
          p_issue_date: s.issueDate,
          p_due_date: s.dueDate,
          p_notes: s.notes,
          p_is_sales_receipt: s.isSalesReceipt,
          p_idempotency_key: idempotencyKey,
          p_linked_work_order_id: null,
          p_tax_rate: taxRateArg,
          p_client_name: s.clientName,
          p_client_email: s.clientEmail,
          p_client_phone: s.clientPhone,
          p_client_address: s.clientAddress,
          p_linked_project_id: null,
          p_source_quote_id: s.sourceQuoteId,
          p_payment_terms_days: paymentTermsArg,
          p_deposit_percentage: depositArg,
          p_late_fee_policy: lateFeePolicyArg,
          p_late_fee_amount: lateFeeAmountArg,
        });
        if (result.success) {
          setDraftInvoiceId(result.data.invoice_id);
          setDraftInvoiceNumber(result.data.invoice_number);
          window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
          setLastSavedAt(new Date().toISOString());
          return {
            success: true,
            invoiceId: result.data.invoice_id,
            invoiceNumber: result.data.invoice_number,
          };
        }
        setError(new Error(result.error.code));
        return { success: false, error: result.error.code };
      }

      const result = await callMoneyRpc<{ invoice_id: string; updated_fields: string[] }>('update_invoice', {
        p_invoice_id: draftInvoiceId,
        p_line_items: linesToCents(s.lines),
        p_issue_date: s.issueDate,
        p_due_date: s.dueDate,
        p_notes: s.notes,
        p_client_id: s.clientId,
        p_client_name: s.clientName,
        p_client_email: s.clientEmail,
        p_client_phone: s.clientPhone,
        p_client_address: s.clientAddress,
        p_tax_rate: taxRateArg,
        p_is_sales_receipt: s.isSalesReceipt,
        p_idempotency_key: idempotencyKey,
        p_linked_work_order_id: null,
        p_linked_project_id: null,
        p_payment_terms_days: paymentTermsArg,
        p_deposit_percentage: depositArg,
        p_late_fee_policy: lateFeePolicyArg,
        p_late_fee_amount: lateFeeAmountArg,
      });
      if (result.success) {
        window.dispatchEvent(new Event(MONEY_MUTATION_EVENT));
        setLastSavedAt(new Date().toISOString());
        return { success: true, invoiceId: draftInvoiceId };
      }
      setError(new Error(result.error.code));
      return { success: false, error: result.error.code };
    } finally {
      setIsAutoSaving(false);
      inFlightRef.current = false;
    }
  }, [businessId, draftInvoiceId, idempotencyKey]);

  useEffect(() => {
    const serialized = JSON.stringify(state);
    if (serialized === lastSerializedState.current) return;
    lastSerializedState.current = serialized;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void save(); }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [state, save]);

  return { save, isAutoSaving, lastSavedAt, draftInvoiceId, draftInvoiceNumber, error };
}
